import { useMemo } from 'react';
import { revalidateLogic, useForm } from '@tanstack/react-form';
import { loginSchema, loginSchemaWithTwoFactorCode } from '../schemas/auth';
import { ValidateFormRefObject } from './useAuthForm';
import { useTanstackFormErrors } from '#app/hooks/useTanstackFormErrors';

/** Серверные ошибки снимаются в handleSubmit (clearServerErrors) — как useAccountTanstackForm. */

interface UseLoginFormParams {
  initialEmail?: string;
  /** После ответа сервера о 2FA — схема с обязательным `code` (как единый Zod на форме в обязательностях). */
  requireTwoFactorCode?: boolean;
  onSubmit: (values: Record<string, any>, obj: ValidateFormRefObject) => void;
}

export function useLoginForm({
  initialEmail = '',
  requireTwoFactorCode = false,
  onSubmit,
}: UseLoginFormParams) {
  const validationSchema = useMemo(
    () => (requireTwoFactorCode ? loginSchemaWithTwoFactorCode : loginSchema),
    [requireTwoFactorCode]
  );

  const tanstackForm = useForm({
    defaultValues: {
      email: initialEmail,
      password: '',
      captchaValue: '',
      form: 'client',
      code: '',
    },
    validationLogic: revalidateLogic({
      mode: 'change',
      modeAfterSubmission: 'change',
    }),
    validators: {
      onChange: validationSchema as any,
      onSubmit: validationSchema as any,
    },
    canSubmitWhenInvalid: true,
    onSubmit: handleFormSubmit,
  });

  const { setServerErrors, clearServerErrors, hasClientErrors, hasServerErrors } =
    useTanstackFormErrors(tanstackForm);

  function handleFormSubmit({ value }: { value: Record<string, string> }) {
    if (hasClientErrors && !hasServerErrors) return;
    onSubmit(value, validateFormObj);
  }
  // Объект-адаптер для передачи в саги (withValidateForm из laravel-request).
  // onSubmit вызывается позже (при сабмите), validateFormObj к тому моменту уже объявлен.
  const validateFormObj: ValidateFormRefObject = {
    state: { formErrors: {} },
    setState: (updater) => {
      if (typeof updater === 'function') {
        const next = updater({ formErrors: {} });
        setServerErrors(next?.formErrors ?? {});
      }
    },
  };

  const handleSubmit = () => {
    clearServerErrors();
    tanstackForm.handleSubmit();
  };

  return {
    tanstackForm,
    handleSubmit,
  };
}
